import {
    AutomaticData,
    ContainerFields,
    ContainerList,
    ContentList,
    SearchContainer,
    SearchInput,
    ClearButton,
    ButtonsContainer,
    ActionButton,
    UserCountText,
    TableHeader,
    TableBody,
    ExportButton,
    ContainerLoader,
    Animation,
    DropDownMenuContainer,
    UserTypeSelect
} from "./style"
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useToast } from '../../../hooks/toast';
import NavigationBar, { NavigationBarProps } from "../../../components/NavigationBar";
import api from "../../../services/api";
import List from "../../../components/List";
import ListItem from "../../../components/List/ListItem";
import ListColumn from "../../../components/List/ListColumn";
import ListText from "../../../components/List/ListText";
import { formatDateToDateRead } from "../../../utils/formatDateToDateRead";
import { TbArrowBack, TbDownload, TbMailFast, TbPencil, TbUserPlus } from "react-icons/tb";
import { User } from "../../../interfaces/User";
import AvatarCange from "../../../components/AvatarCange";
import getTypeUserDescription from "../../../utils/getTypeUserDescription";
import Papa from "papaparse";
import { InvitedUser } from "../../../interfaces/InvitedUser";
import { AiOutlineCloseCircle, AiOutlineSearch } from "react-icons/ai";
import { IoIosCloseCircle } from "react-icons/io";
import Avatar from "react-avatar";
import InviteTeam from "../../../dialogs/InviteTeam";
import Lottie from "lottie-web";
import DropDownList from "../../../components/DropDownList";
import DropDownItem from "../../../components/DropDownList/DropDownItem";
import { HiDotsHorizontal } from "react-icons/hi";
import UserConfig from "../../../dialogs/UserConfig";
import { useAuth } from "../../../hooks/auth";
import DialogAction from "../../../dialogs/DialogAction";

const itemsNavBar: NavigationBarProps = {
    items: [
        {
            label: 'Ativos',
            id: 1,
            segment: 'active',
            active: true
        },
        {
            label: 'Convidados',
            id: 2,
            segment: 'invited',
            active: false
        },
        {
            label: 'Removidos',
            id: 3,
            segment: 'removed',
            active: false
        }
    ]
}

interface MembersProps {
    onClickAddUser?: () => void;
}

const Members: React.FC<MembersProps> = ({ onClickAddUser }) => {

    const { addToast } = useToast();
    const { user } = useAuth();
    const searchInputRef = useRef<HTMLInputElement>(null);

    const [navBarSelected, setNavBarSelected] = useState(1); //1 -> Todos, 2 -> Ativos, 3 -> Convidados, 4 -> Removidos
    const [navBarItems, setNavBarItems] = useState(itemsNavBar.items);

    // Users state variables
    const [allUsers, setAllUsers] = useState<User[]>([]);
    const [filteredUsers, setFilteredUsers] = useState<User[]>([]);

    const [allInvitedUsers, setAllInvitedUsers] = useState<InvitedUser[]>([]);
    const [invitedUsers, setInvitedUsers] = useState<InvitedUser[]>([]);

    const [openInviteTeam, setOpenInviteTeam] = useState(false);
    const [openUserConfig, setOpenUserConfig] = useState(false);
    const [selectedUser, setSelectedUser] = useState<User | undefined>();

    // Search functionality
    const [searchTerm, setSearchTerm] = useState("");

    const lottieContainer = useRef<HTMLDivElement>(null);
    const lottieContainerInvited = useRef<HTMLDivElement>(null);
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [loadingInvitedUsers, setLoadingInvitedUsers] = useState(false);
    const [loadingDelete, setLoadingDelete] = useState(false);
    const [loadingInviting, setLoadingInviting] = useState(false);
    const [openDialogDelUser, setOpenDialogDelUser] = useState(false);

    // Add state to track changes in progress
    const [changingUserTypes, setChangingUserTypes] = useState<{ [key: number]: string }>({});

    const getUsersApi = useCallback(async () => {
        setLoadingUsers(true);
        await api.get('/user/all')
            .then(response => {
                if (response.data !== null) {
                    let apiRetUsers: User[] = response.data;

                    //Calculate all active and all inactive users
                    let allActiveUsers = apiRetUsers.filter((user) => user.status !== "B").length;
                    let allInactiveUsers = apiRetUsers.filter((user) => user.status === "B").length;

                    // Usar o padrão de atualização funcional do estado para evitar dependência circular
                    setNavBarItems(prevItems => {
                        return prevItems.map(item => {
                            if (item.id === 1) { //Active users
                                return {
                                    ...item,
                                    badge: allActiveUsers !== undefined ? String(allActiveUsers) : undefined,
                                }
                            } else if (item.id === 3) { //Inactive users
                                return {
                                    ...item,
                                    badge: allInactiveUsers !== undefined ? String(allInactiveUsers) : undefined,
                                }
                            } else {
                                return item;
                            }
                        });
                    });

                    setAllUsers(apiRetUsers);
                    setFilteredUsers(apiRetUsers);
                }
                setLoadingUsers(false);
            })
            .catch(error => {
                setLoadingUsers(false);
                addToast({
                    type: 'error',
                    title: 'Erro ao buscar os usuários',
                    description: 'Não foi possível buscar os usuários, tente novamente mais tarde!'
                });
            });
    }, [addToast]);

    const getInvitedUsers = useCallback(async () => {
        setLoadingInvitedUsers(true);
        api.get('/invite-user?withUserInviter=true')
            .then(response => {
                if (response.data !== null) {
                    let apiRetInvited: InvitedUser[] = response.data;

                    let allInvitedUsers = apiRetInvited.length;

                    setNavBarItems(prevItems => {
                        return prevItems.map(item => {
                            if (item.id === 2) { //Active users
                                return {
                                    ...item,
                                    badge: allInvitedUsers !== undefined ? String(allInvitedUsers) : undefined,
                                }
                            } else {
                                return item;
                            }
                        });
                    });

                    setAllInvitedUsers(apiRetInvited);
                    setInvitedUsers(apiRetInvited);
                }
                setLoadingInvitedUsers(false);
            })
            .catch(error => {
                setLoadingInvitedUsers(false);
                addToast({
                    type: 'error',
                    title: 'Erro ao buscar os usuários convidados',
                    description: 'Não foi possível buscar os usuários convidados, tente novamente mais tarde!'
                });
            });

    }, [addToast]);

    // Function to update data after closing the InviteTeam dialog
    const updateData = useCallback(() => {
        getUsersApi();
        getInvitedUsers();
    }, [getUsersApi, getInvitedUsers]);

    const handleRemoveInvitedUser = useCallback(async (email: string) => {

        setLoadingInviting(true);

        await api
            .delete('/invite-user?email=' + email)
            .then(response => {

                updateData();
                setLoadingInviting(false);

            }).catch(error => {
                setLoadingInviting(false);
                addToast({
                    type: 'error',
                    title: 'Erro ao deletar o convidado',
                    description: 'Ocorreu um erro ao deletar o usuário da base de dados!',
                });
            });

    }, [addToast, updateData]);

    const handleInviteNewUser = useCallback(async (email: string) => {

        setLoadingInviting(true);

        await api
            .post('/invite-user', {
                email,
                name: email.substr(0, email.indexOf("@")),
            })
            .then(response => {

                updateData();

                addToast({
                    type: 'success',
                    title: 'E-mail enviado com sucesso!',
                    description: 'Em instantes o e-mail estará chegando na caixa do seu convidado ;)',
                });
                setLoadingInviting(false);
            }).catch(error => {
                setLoadingInviting(false);
                addToast({
                    type: 'error',
                    title: 'Erro ao reenviar e-mail ao Usuário',
                    description: 'Ocorreu um erro ao reenviar o e-mail ao usuário!',
                });
            });

    }, [addToast, updateData]);

    // Filter users based on search term
    const filterUsers = useCallback(() => {
        if (navBarSelected === 2) {
            if (!searchTerm.trim()) {
                setInvitedUsers(allInvitedUsers);
                return;
            }

            const searchTermLower = searchTerm.toLowerCase();
            const filtered = allInvitedUsers.filter(user => {
                return (
                    (user.name && user.name.toLowerCase().includes(searchTermLower)) ||
                    (user.email && user.email.toLowerCase().includes(searchTermLower))
                );
            });

            setInvitedUsers(filtered);
        } else {
            if (!searchTerm.trim()) {
                setFilteredUsers(allUsers);
                return;
            }

            const searchTermLower = searchTerm.toLowerCase();
            const filtered = allUsers.filter(user => {
                return (
                    (user.name && user.name.toLowerCase().includes(searchTermLower)) ||
                    (user.email && user.email.toLowerCase().includes(searchTermLower)) ||
                    (getTypeUserDescription(user.type).toLowerCase().includes(searchTermLower)) ||
                    ((user.status !== "B" ? "ativo" : "removido").includes(searchTermLower))
                );
            });

            setFilteredUsers(filtered);
        }
    }, [allUsers, searchTerm, allInvitedUsers, navBarSelected]);

    // Handle search input
    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.target.value);
    };

    // Clear search
    const clearSearch = () => {
        setSearchTerm("");
        if (searchInputRef.current) {
            searchInputRef.current.focus();
        }
    };

    // Handle keyboard events
    const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Escape") {
            clearSearch();
        }
    };

    // Handle export to CSV
    const handleExportCSV = () => {
        const usersToExport = filteredUsers.filter(user =>
            navBarSelected === 1 ? user.status !== "B" :
                navBarSelected === 3 ? user.status === "B" :
                    true
        );

        if (usersToExport.length === 0) {
            addToast({
                type: 'info',
                title: 'Nenhum dado para exportar',
                description: 'Não há usuários na lista atual para exportar.'
            });
            return;
        }

        // Prepare data for CSV export
        const csvData = usersToExport.map(user => ({
            Nome: user.name,
            Email: user.email || "",
            Perfil: getTypeUserDescription(user.type),
            Status: user.status !== "B" ? "Ativo" : "Removido",
            'Criado em': user.dt_created ? user.dt_created : "",
            'Último acesso': user.last_login ? user.last_login : "Não encontrado"
        }));

        // Convert to CSV
        const csv = Papa.unparse(csvData);

        // Create a download link
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `usuarios_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const dialogReactiveUserSubmmit = useCallback(async (user: User) => {

        if (user !== undefined) {
            setLoadingDelete(true);
            api
                .post('/user/reactive', {
                    id_user: user.id_user
                })
                .then(response => {

                    updateData();
                    setSelectedUser(undefined);

                    addToast({
                        type: 'success',
                        title: 'Usuário reativado com sucesso!',
                        description: 'O usuário selecionado foi reativado do seu ambiente com sucesso',
                    });

                    setLoadingDelete(false);

                }).catch(error => {
                    setLoadingDelete(false);
                    console.log(error.response.data);
                    addToast({
                        type: 'error',
                        title: 'Erro ao reativar o Usuário',
                        description: 'Ocorreu um erro ao reativar o usuário!',
                    });
                });
        }

    }, [addToast, updateData]);

    const dialogDelUserSubmmit = useCallback(async () => {

        // If is trying to change the type of the user to Admin
        if (selectedUser !== undefined && selectedUser.id_user === user.id_user) {
            addToast({
                type: 'error',
                title: 'Erro ao excluir o usuário',
                description: 'Não é possível excluir o seu próprio usuário!'
            });
            return;
        }

        if (selectedUser !== undefined) {
            setLoadingDelete(true);
            api
                .post('/user/inactive', {
                    id_user: selectedUser.id_user
                })
                .then(response => {

                    updateData();

                    setOpenDialogDelUser(false);
                    setSelectedUser(undefined);

                    addToast({
                        type: 'success',
                        title: 'Usuário removido com sucesso!',
                        description: 'O usuário selecionado foi removido do seu ambiente com sucesso',
                    });

                    setLoadingDelete(false);

                }).catch(error => {
                    setLoadingDelete(false);
                    console.log(error.response.data);
                    addToast({
                        type: 'error',
                        title: 'Erro ao remover Usuário',
                        description: 'Ocorreu um erro ao remover o usuário!',
                    });
                });
        }

    }, [addToast, updateData, selectedUser, user.id_user]);

    // Apply filter when search term changes
    useEffect(() => {
        filterUsers();
    }, [filterUsers, searchTerm]);

    // Add keyboard shortcut for search focus
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'f') {
                e.preventDefault();
                searchInputRef.current?.focus();
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, []);

    useEffect(() => {
        getUsersApi();
        getInvitedUsers();
    }, [getUsersApi, getInvitedUsers]);

    useEffect(() => {
        if (lottieContainer.current) {
            Lottie.loadAnimation({
                container: lottieContainer.current,
                renderer: 'svg',
                loop: true,
                autoplay: true,
                animationData: require('../../../assets/lottie/loader.json')
            })
        }
    }, [loadingUsers]);

    useEffect(() => {
        if (lottieContainerInvited.current) {
            Lottie.loadAnimation({
                container: lottieContainerInvited.current,
                renderer: 'svg',
                loop: true,
                autoplay: true,
                animationData: require('../../../assets/lottie/loader.json')
            })
        }
    }, [loadingInvitedUsers]);

    // Filter users based on the selected tab
    const displayedUsers = filteredUsers.filter(user => {
        if (navBarSelected === 1) return user.status !== "B"; // Active
        if (navBarSelected === 3) return user.status === "B"; // Removed
        return true; // All other tabs
    });

    // Add this function to handle user type changes
    const handleEditTypeUser = useCallback(async (e: React.FormEvent<HTMLSelectElement>, userToEdit: User) => {
        const newType = e.currentTarget.value;
        const validUsersAdmin = allUsers.filter((u) => u.type === "A" && u.status !== "B");

        // If is trying to change the type of the user to Admin
        if (userToEdit.id_user === user.id_user && newType !== "A") {
            addToast({
                type: 'error',
                title: 'Erro ao editar o usuário',
                description: 'Não é possível alterar o seu tipo do usuário para não Administrador!'
            });
            return;
        }

        // If the only admin user is trying to change their type
        if (validUsersAdmin.length === 1 &&
            validUsersAdmin[0].id_user === userToEdit.id_user &&
            newType !== "A") {
            addToast({
                type: 'error',
                title: 'Erro ao editar o usuário',
                description: 'É necessário ter ao menos um Administrador no ambiente!',
            });
            return;
        }

        // Update local state immediately for responsive UI
        setChangingUserTypes(prev => ({
            ...prev,
            [userToEdit.id_user]: newType
        }));

        try {
            setLoadingUsers(true);
            await api.post('/user', {
                id_user: userToEdit.id_user,
                type: newType
            });

            // On success, update the actual data
            setAllUsers(prevUsers =>
                prevUsers.map(u =>
                    u.id_user === userToEdit.id_user
                        ? { ...u, type: newType }
                        : u
                )
            );

            setFilteredUsers(prevUsers =>
                prevUsers.map(u =>
                    u.id_user === userToEdit.id_user
                        ? { ...u, type: newType }
                        : u
                )
            );

            addToast({
                type: 'success',
                title: 'Perfil atualizado',
                description: 'O perfil do usuário foi atualizado com sucesso!'
            });

        } catch (error) {
            addToast({
                type: 'error',
                title: 'Erro ao editar o usuário',
                description: error.response?.data?.message || 'Ocorreu um erro ao editar o usuário na base de dados!'
            });
        } finally {
            setLoadingUsers(false);
            // Remove from changing state after operation completes
            setChangingUserTypes(prev => {
                const updated = { ...prev };
                delete updated[userToEdit.id_user];
                return updated;
            });
        }

    }, [addToast, allUsers, user]);

    return (
        <ContentList>
            {/* Forms */}
            <ContainerFields item xs={12} md={12} lg={12} sm={12}>
                <NavigationBar
                    items={navBarItems}
                    navBarSelected={navBarSelected}
                    setNavBarSelected={setNavBarSelected}
                />

                {/* Search and Action Buttons */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px', marginTop: '15px' }}>
                    <SearchContainer>
                        <AiOutlineSearch />
                        <SearchInput
                            ref={searchInputRef}
                            type="text"
                            placeholder="Buscar por nome, email, perfil ou status..."
                            value={searchTerm}
                            onChange={handleSearchChange}
                            onKeyDown={handleSearchKeyDown}
                        />
                        {searchTerm && (
                            <ClearButton onClick={clearSearch}>
                                <IoIosCloseCircle />
                            </ClearButton>
                        )}
                    </SearchContainer>

                    <ButtonsContainer>
                        <ExportButton onClick={handleExportCSV} icon={TbDownload}>
                            Exportar CSV
                        </ExportButton>
                        {onClickAddUser && (
                            <ActionButton onClick={() => setOpenInviteTeam(true)} icon={TbUserPlus}>
                                Adicionar usuário
                            </ActionButton>
                        )}
                    </ButtonsContainer>
                </div>

                {(navBarSelected === 1 || navBarSelected === 3) && (
                    <>
                        <ContainerList>
                            <TableHeader>
                                <List>
                                    <ListItem>
                                        <ListColumn flex flexGrow={2}>
                                            <ListText header>
                                                Nome
                                            </ListText>
                                        </ListColumn>
                                        <ListColumn>
                                            <ListText header>
                                                Identificador
                                            </ListText>
                                        </ListColumn>
                                        <ListColumn>
                                            <ListText header>
                                                Perfil
                                            </ListText>
                                        </ListColumn>
                                        <ListColumn>
                                            <ListText header>
                                                Status
                                            </ListText>
                                        </ListColumn>
                                        <ListColumn>
                                            <ListText header>
                                                Criado em
                                            </ListText>
                                        </ListColumn>
                                        <ListColumn>
                                            <ListText header>
                                                Último acesso
                                            </ListText>
                                        </ListColumn>
                                        <ListColumn flex flexGrow={0.5}>
                                            <ListText header>
                                                Ações
                                            </ListText>
                                        </ListColumn>
                                    </ListItem>
                                </List>
                            </TableHeader>

                            <TableBody>
                                <List style={{ minHeight: '300px !important' }}>
                                    {displayedUsers.length > 0 ? (
                                        displayedUsers.sort((a, b) => a.name.localeCompare(b.name))
                                            .map((user, index) => {
                                                return (
                                                    <ListItem key={index}>
                                                        <ListColumn flex flexGrow={2} aglinItems="flex-start">
                                                            <ListText fontSize="15px" fontWeight="500">
                                                                <AvatarCange user={user} size="35" />
                                                                <div style={{ marginLeft: '10px', display: 'flex', flexDirection: 'column' }}>
                                                                    {user.name}
                                                                    {user.email !== undefined && user.email !== null && user.email !== "" && (
                                                                        <span style={{ fontSize: '12px', color: '#A0A0A0', marginTop: '1px' }}>
                                                                            {user.email}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </ListText>
                                                        </ListColumn>
                                                        <ListColumn>
                                                            <ListText>
                                                                <AutomaticData>
                                                                    {user.id_user}
                                                                </AutomaticData>
                                                            </ListText>
                                                        </ListColumn>
                                                        <ListColumn>
                                                            {navBarSelected === 1 ? (
                                                                <UserTypeSelect
                                                                    name="select"
                                                                    value={changingUserTypes[user.id_user] || user.type}
                                                                    onChange={e => handleEditTypeUser(e, user)}
                                                                    disabled={!!changingUserTypes[user.id_user]}
                                                                >
                                                                    <option value="A">Administrador</option>
                                                                    <option value="M">Membro</option>
                                                                    <option value="I">Membro Individual</option>
                                                                    <option value="S">Solicitante</option>
                                                                    <option value="V">Visualizador</option>
                                                                </UserTypeSelect>
                                                            ) : (
                                                                <ListText>
                                                                    {getTypeUserDescription(user.type)}
                                                                </ListText>
                                                            )}
                                                        </ListColumn>
                                                        <ListColumn>
                                                            <ListText>
                                                                {user.status !== "B" ? "Ativo" : "Removido"}
                                                            </ListText>
                                                        </ListColumn>
                                                        <ListColumn>
                                                            <ListText>
                                                                <AutomaticData>
                                                                    {formatDateToDateRead(new Date(user.dt_created))}
                                                                </AutomaticData>
                                                            </ListText>
                                                        </ListColumn>
                                                        <ListColumn>
                                                            <ListText>
                                                                <AutomaticData>
                                                                    {user.last_login !== undefined && user.last_login !== null ? formatDateToDateRead(new Date(user.last_login)) : "Não encontrado"}
                                                                </AutomaticData>
                                                            </ListText>
                                                        </ListColumn>
                                                        <ListColumn flex flexGrow={0.5}>
                                                            {navBarSelected === 1 && (
                                                                <DropDownMenuContainer>
                                                                    <DropDownList color={"#f4f4f4"} icon={HiDotsHorizontal}>
                                                                        <DropDownItem title={"Editar o usuário"} icon={TbPencil} color={"#9e37ed"} onClick={() => {
                                                                            setSelectedUser(user);
                                                                            setOpenUserConfig(true);
                                                                        }} />
                                                                        <DropDownItem title={"Remover o usuário"} icon={AiOutlineCloseCircle} color={"#9e37ed"} onClick={() => {
                                                                            setSelectedUser(user);
                                                                            setOpenDialogDelUser(true);
                                                                        }} />
                                                                    </DropDownList>
                                                                </DropDownMenuContainer>
                                                            )}
                                                            {navBarSelected === 3 && (
                                                                <DropDownMenuContainer>
                                                                    <DropDownList color={"#f4f4f4"} icon={HiDotsHorizontal}>
                                                                        <DropDownItem title={"Reativar o usuário"} icon={TbArrowBack} color={"#9e37ed"} onClick={() => dialogReactiveUserSubmmit(user)} />
                                                                    </DropDownList>
                                                                </DropDownMenuContainer>
                                                            )}
                                                        </ListColumn>
                                                    </ListItem>
                                                )
                                            })
                                    ) : !loadingUsers ? (
                                        <ListItem>
                                            <ListColumn flex flexGrow={1}>
                                                <ListText>
                                                    Nenhum usuário encontrado
                                                </ListText>
                                            </ListColumn>
                                        </ListItem>
                                    ) : (
                                        <ContainerLoader>
                                            <Animation className="lottieContainer" ref={lottieContainer} />
                                        </ContainerLoader >
                                    )}
                                </List>
                            </TableBody>
                        </ContainerList>

                        {/* User count */}
                        <UserCountText>
                            Exibindo {displayedUsers.length} {displayedUsers.length === 1 ? 'usuário' : 'usuários'}
                            {searchTerm ? ` para a busca "${searchTerm}"` : ''}
                        </UserCountText>
                    </>
                )}

                {navBarSelected === 2 && (
                    <>
                        <ContainerList>
                            <TableHeader>
                                <List>
                                    <ListItem>
                                        <ListColumn flex flexGrow={2}>
                                            <ListText header>
                                                Nome
                                            </ListText>
                                        </ListColumn>
                                        <ListColumn>
                                            <ListText header>
                                                Perfil
                                            </ListText>
                                        </ListColumn>
                                        <ListColumn>
                                            <ListText header>
                                                Status
                                            </ListText>
                                        </ListColumn>
                                        <ListColumn>
                                            <ListText header>
                                                Criado em
                                            </ListText>
                                        </ListColumn>
                                        <ListColumn>
                                            <ListText header>
                                                Convidado por
                                            </ListText>
                                        </ListColumn>
                                        <ListColumn>
                                            <ListText header>
                                                E-mails enviados
                                            </ListText>
                                        </ListColumn>
                                        <ListColumn>
                                            <ListText header>
                                                Ações
                                            </ListText>
                                        </ListColumn>
                                    </ListItem>
                                </List>
                            </TableHeader>

                            <TableBody>
                                <List>
                                    {invitedUsers.length > 0 ? (
                                        invitedUsers.sort((a, b) => a.name.localeCompare(b.name))
                                            .map((invited, index) => {
                                                return (
                                                    <ListItem key={index}>
                                                        <ListColumn flex flexGrow={2} aglinItems="flex-start">
                                                            <ListText fontSize="15px" fontWeight="500">
                                                                <Avatar color={"#9e37ee"} size={"35"} name={invited.name} round="100%" />
                                                                <div style={{ marginLeft: '10px', display: 'flex', flexDirection: 'column' }}>
                                                                    {invited.name}
                                                                    {invited.email !== undefined && invited.email !== null && invited.email !== "" && (
                                                                        <span style={{ fontSize: '12px', color: '#A0A0A0', marginTop: '1px' }}>
                                                                            {invited.email}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </ListText>
                                                        </ListColumn>
                                                        <ListColumn>
                                                            <ListText>
                                                                {getTypeUserDescription(invited.type)}
                                                            </ListText>
                                                        </ListColumn>
                                                        <ListColumn>
                                                            <ListText>
                                                                {"Convidado"}
                                                            </ListText>
                                                        </ListColumn>
                                                        <ListColumn>
                                                            <ListText>
                                                                <AutomaticData>
                                                                    {formatDateToDateRead(new Date(invited.dt_created))}
                                                                </AutomaticData>
                                                            </ListText>
                                                        </ListColumn>
                                                        <ListColumn>
                                                            <ListText>
                                                                {invited.user_inviter.name}
                                                            </ListText>
                                                        </ListColumn>
                                                        <ListColumn>
                                                            <ListText>
                                                                {invited.send_mail_count}
                                                            </ListText>
                                                        </ListColumn>
                                                        <ListColumn>
                                                            <DropDownMenuContainer>
                                                                <DropDownList color={"#f4f4f4"} icon={HiDotsHorizontal}>
                                                                    <DropDownItem title={"Reenviar o convite"} icon={TbMailFast} color={"#9e37ed"} onClick={() => !loadingInviting ? handleInviteNewUser(invited.email) : {}} />
                                                                    <DropDownItem title={"Cancelar o convite"} icon={AiOutlineCloseCircle} color={"#9e37ed"} onClick={() => !loadingInviting ? handleRemoveInvitedUser(invited.email) : {}} />
                                                                </DropDownList>
                                                            </DropDownMenuContainer>
                                                        </ListColumn>
                                                    </ListItem>
                                                )
                                            })
                                    ) : !loadingInvitedUsers ? (
                                        <ListItem>
                                            <ListColumn flex flexGrow={1}>
                                                <ListText>
                                                    Nenhum usuário convidado encontrado
                                                </ListText>
                                            </ListColumn>
                                        </ListItem>
                                    ) : (
                                        <ContainerLoader>
                                            <Animation className="lottieContainerInvited" ref={lottieContainerInvited} />
                                        </ContainerLoader >
                                    )}
                                </List>
                            </TableBody>
                        </ContainerList>

                        {/* User count */}
                        <UserCountText>
                            Exibindo {invitedUsers.length} {invitedUsers.length === 1 ? 'usuário' : 'usuários'}
                            {searchTerm ? ` para a busca "${searchTerm}"` : ''}
                        </UserCountText>
                    </>
                )}

                <InviteTeam
                    key={'invite-team'}
                    open={openInviteTeam}
                    onClose={() => {
                        setOpenInviteTeam(false);
                        updateData();
                    }}
                />

                {selectedUser !== undefined && selectedUser !== null && selectedUser.id_user !== undefined && (
                    <UserConfig
                        key={'user-config'}
                        open={openUserConfig}
                        onClose={() => {
                            setOpenUserConfig(false);
                            updateData();
                        }}
                        user_id={selectedUser.id_user}
                    />
                )}

                <DialogAction
                    key="delete-users-environment"
                    open={openDialogDelUser}
                    message={"Você tem certeza que deseja remover este usuário?"}
                    actions_items={[
                        "Remoção da permissão de acesso ao ambiente;",
                        "Alteração do estado do usuário para Removido;",
                        "Exclusão do usuário em todos os cartões em que é responsável;",
                        "Exclusão de todas as permissões de acesso na plataforma;",
                    ]}
                    disclaimer={"Lembrando que após a confirmação não será mais possível recuperar os vínculos citados acima"}
                    isLoading={loadingDelete}
                    onSubmmit={dialogDelUserSubmmit}
                    onClose={() => setOpenDialogDelUser(false)}
                    type={1}
                />

            </ContainerFields>
        </ContentList>
    );
}

export default Members;